/**
 * V112-4-7 (BL-45, ADR-0098): the suggested Control impact of a law / court decision, on its review-queue row.
 *
 *   ControlImpactPanel      the suggestion, every duty with its outcome, the flags (with "Correct jurisdiction" and
 *                           "Add the statute link"), the source link and the analysis date. While approving, the ticks
 *                           ARE the decision: amend / new duties start ticked, covered / not-analysed ones can be ticked
 *                           to file a new control. Without an analysis: Re-run, or tick "File a new control for this law".
 *   OverrideNote            what the suggestion would file and what will be filed now, with "Use the suggestion".
 *   FiledNotice             after apply: "Filed: …" in a region that takes focus (announced once, by focus).
 *   FiledSummary            "Filed: 1 spec amendment, 1 new control", each linked to its proposal, plus what was skipped.
 *   ControlImpactStatsLine  the owner stat: how often the suggestion is kept.
 *
 * The suggestion only preselects; nothing here approves or files. Status is words plus a glyph, never colour alone,
 * and a glyph never wraps away from its words.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Field, Input, StatusGlyph, type StatusGlyphKind } from '@aigovops/design-system';
import { intervalWords, type DutyThreshold, type ImpactDuty, type ImpactOutcome } from '@aigovops/core';
import { Link, useLocation } from 'react-router-dom';
import { ApiError, type ControlImpactFiling, type ControlImpactStats, type ControlImpactView } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import './control-impact.css';

export const CONTROL_IMPACT_LABEL: Record<ImpactOutcome, string> = {
  informational: 'Informational only',
  spec_amendment: 'Spec amendment',
  new_control: 'New control',
};

// ── Reading the stored analysis ──────────────────────────────────────────────

/** A row's `impact.control_impact`, or null when the row has none (old rows) or it is malformed. */
export function readControlImpact(rawImpact: string | null | undefined): ControlImpactView | null {
  try {
    const parsed = JSON.parse(rawImpact ?? '') as { control_impact?: unknown };
    return parsed && typeof parsed === 'object' ? normalizeControlImpact(parsed.control_impact) : null;
  } catch {
    return null;
  }
}

/** A parsed `control_impact` with its lists defaulted, or null when it isn't one. */
export function normalizeControlImpact(ci: unknown): ControlImpactView | null {
  if (!ci || typeof ci !== 'object') return null;
  const v = ci as ControlImpactView;
  if (v.status !== 'ready' && v.status !== 'pending' && v.status !== 'failed') return null;
  return {
    ...v,
    duties: Array.isArray(v.duties) ? v.duties : [],
    flags: Array.isArray(v.flags) ? v.flags : [],
    already_covered_by: Array.isArray(v.already_covered_by) ? v.already_covered_by : [],
    duties_over_cap: typeof v.duties_over_cap === 'number' ? v.duties_over_cap : 0,
  };
}

// ── The ticks decide (mirrors core outcomeForKept) ───────────────────────────

/** The suggestion's ticks: amend and new duties. */
export function defaultTicks(ci: ControlImpactView | null): number[] {
  if (!ci || ci.status !== 'ready') return [];
  return ci.duties.flatMap((d, i) => (d.outcome === 'amend' || d.outcome === 'new' ? [i] : []));
}

/**
 * Ticks for a new analysis: a duty whose text the editor already ticked or unticked keeps that choice; any other duty
 * starts on the suggestion.
 */
export function carryTicks(ci: ControlImpactView | null, editorChoices: ReadonlyMap<string, boolean>): number[] {
  const defaults = new Set(defaultTicks(ci));
  if (!ci || ci.status !== 'ready') return [];
  return ci.duties.flatMap((d, i) => {
    if (d.outcome === 'not_deployer') return [];
    const chosen = editorChoices.get(d.text);
    return (chosen ?? defaults.has(i)) ? [i] : [];
  });
}

const files = (d: ImpactDuty | undefined): 'spec_amendment' | 'new_control' | null =>
  !d || d.outcome === 'not_deployer' ? null : d.outcome === 'amend' ? 'spec_amendment' : d.draft_control ? 'new_control' : null;

/** The recorded outcome follows the ticks (core outcomeForKept): any other filable duty → new control; else any amend → spec amendment. */
export function outcomeForTicks(duties: readonly ImpactDuty[], kept: Iterable<number>): ImpactOutcome {
  const k = [...kept].map((i) => files(duties[i]));
  if (k.includes('new_control')) return 'new_control';
  if (k.includes('spec_amendment')) return 'spec_amendment';
  return 'informational';
}

/** "1 spec amendment + 1 new control", or null when the ticks file nothing. */
export function willFile(duties: readonly ImpactDuty[], kept: Iterable<number>): string | null {
  const k = [...kept].map((i) => files(duties[i]));
  const amend = k.filter((x) => x === 'spec_amendment').length;
  const fresh = k.filter((x) => x === 'new_control').length;
  const parts = [
    amend ? `${amend} spec amendment${amend === 1 ? '' : 's'}` : null,
    fresh ? `${fresh} new control${fresh === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' + ') : null;
}

/** "Approve & file 1 spec amendment + 1 new control", or "Approve (nothing to file)". */
export function confirmLabel(duties: readonly ImpactDuty[], kept: Iterable<number>): string {
  const w = willFile(duties, kept);
  return w ? `Approve & file ${w}` : 'Approve (nothing to file)';
}

export function sameTicks(a: ReadonlySet<number>, b: readonly number[]): boolean {
  return a.size === b.length && b.every((i) => a.has(i));
}

// ── Words ────────────────────────────────────────────────────────────────────

const dutySeconds = (t: DutyThreshold | null | undefined): number | null =>
  t?.kind === 'review_every_days' ? t.value * 86_400 : t?.kind === 'review_every_hours' ? t.value * 3_600 : null;

/**
 * "evidence at least every 3 hours (now 365 days)"; "accuracy at least 0.9 (now 0.8)". Never weaker than the duty: the
 * shown target is the stricter of the computed value and the duty's own threshold.
 */
export function amendmentWords(d: Pick<ImpactDuty, 'spec_field' | 'current_value' | 'proposed_value' | 'threshold'>): string | null {
  if (d.spec_field === 'freshness_max_age_seconds') {
    const options = [typeof d.proposed_value === 'number' ? d.proposed_value : null, dutySeconds(d.threshold)].filter((x): x is number => x !== null && x > 0);
    if (!options.length) return null;
    const now = typeof d.current_value === 'number' ? intervalWords(d.current_value) : 'no limit';
    return `evidence at least every ${intervalWords(Math.min(...options))} (now ${now})`;
  }
  if (d.spec_field === 'threshold_default') {
    const next = (d.proposed_value ?? {}) as Record<string, unknown>;
    const was = (d.current_value ?? {}) as Record<string, unknown>;
    const t = d.threshold;
    const bound: 'min' | 'max' = t?.kind === 'metric_max' ? 'max' : t?.kind === 'metric_min' ? 'min' : typeof next.max === 'number' && next.max !== was.max ? 'max' : 'min';
    const candidates = [typeof next[bound] === 'number' ? (next[bound] as number) : null, t && t.kind !== 'review_every_days' && t.kind !== 'review_every_hours' ? t.value : null]
      .filter((x): x is number => x !== null);
    if (!candidates.length) return null;
    const target = bound === 'min' ? Math.max(...candidates) : Math.min(...candidates);
    const metricRaw = typeof next.metric === 'string' ? next.metric : t && 'metric' in t ? t.metric : 'threshold';
    const metric = metricRaw.replace(/_/g, ' ');
    const before = typeof was[bound] === 'number' && (typeof was.metric !== 'string' || was.metric === metricRaw) ? String(was[bound]) : 'none';
    return `${metric} ${bound === 'min' ? 'at least' : 'at most'} ${target} (now ${before})`;
  }
  return null;
}

const DUTY_GLYPH: Record<ImpactDuty['outcome'], StatusGlyphKind> = { covered: 'check', amend: 'half', new: 'circle', not_deployer: 'dots', unanalysed: 'unknown' };

function outcomeWords(d: ImpactDuty): string {
  const control = d.control_title || d.control_id || 'a control';
  if (d.outcome === 'covered') return `Covered by ${control}`;
  if (d.outcome === 'amend') { const w = amendmentWords(d); return `Amend ${control}${w ? `: ${w}` : ''}`; }
  if (d.outcome === 'new') return `New control “${d.draft_control?.title ?? d.text}”`;
  if (d.outcome === 'unanalysed') return 'Not analysed';
  return 'Not your customers’ obligation';
}

/** The checkbox's action words. */
function tickAction(d: ImpactDuty): string {
  if (d.outcome === 'amend') return 'File spec amendment';
  if (d.outcome === 'new') return 'File new control';
  if (d.outcome === 'covered') return 'File a new control instead';
  return 'File a new control';
}

/** Why there is no analysis, in plain words (never the stored error text). */
export function noAnalysisWords(ci: ControlImpactView | null): string {
  if (!ci) return 'Not analysed yet';
  if (ci.status === 'pending') return 'Analysis running';
  return /refus|provider/i.test(ci.error ?? '') ? 'The analysis service didn’t answer' : 'The analysis couldn’t finish';
}

const confidenceWord = (c: number) => (c >= 0.8 ? 'high' : c >= 0.5 ? 'medium' : 'low');
/** A section reference is shown as text; a longer passage is a quote. */
const isSectionRef = (s: string) => s.length <= 40 && /^(sec\.?|section|§|art\.?|article|para\.?|chapter|title|\(?[0-9])/i.test(s.trim());

/** Glyph + words that never wrap apart. */
function Pair({ glyph, children, className, id, ...rest }: { glyph: StatusGlyphKind; children: ReactNode; className?: string; id?: string } & Record<`data-${string}`, string>) {
  return (
    <span id={id} className={`ci-pair${className ? ` ${className}` : ''}`} {...rest}>
      <StatusGlyph kind={glyph} />
      <span className="ci-pair-text">{children}</span>
    </span>
  );
}

// ── Inline corrections (jurisdiction, statute link) ──────────────────────────

function InlineFix(props: {
  id: string;
  openLabel: string;
  fieldLabel: string;
  hint?: string;
  saveLabel: string;
  inputType?: 'text' | 'url';
  placeholder?: string;
  /** Returns an error message, or null when the value can be sent (normalised by `normalise`). */
  validate: (v: string) => string | null;
  normalise?: (v: string) => string;
  onSave: (v: string) => Promise<void>;
  /** After a successful save, focus goes here (the panel heading). */
  focusAfterSave: string;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const returnToOpener = useRef(false);
  useEffect(() => {
    if (open) inputRef.current?.focus();
    else if (returnToOpener.current) { returnToOpener.current = false; openerRef.current?.focus(); }
  }, [open]);
  if (!open) {
    return <Button ref={openerRef} variant="secondary" compact onClick={() => { setOpen(true); setValue(''); setError(null); }}>{props.openLabel}</Button>;
  }
  const save = async () => {
    if (savingRef.current) return; // one request at a time
    const v = (props.normalise ?? ((x: string) => x.trim()))(value);
    const invalid = props.validate(v);
    if (invalid) { setError(invalid); return; }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await props.onSave(v);
      setOpen(false);
      setTimeout(() => document.getElementById(props.focusAfterSave)?.focus(), 0);
    } catch (err) {
      const detail = err instanceof ApiError ? (err.body as { detail?: string } | null)?.detail : null;
      setError(detail ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  return (
    <form className="ci-fix" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <Field id={props.id} label={props.fieldLabel} hint={props.hint} error={error ?? undefined}>
        <Input id={props.id} ref={inputRef} type={props.inputType ?? 'text'} value={value} onChange={(e) => setValue(e.target.value)} placeholder={props.placeholder} autoComplete="off" />
      </Field>
      <span className="ci-actions">
        <Button type="submit" variant="secondary" compact aria-disabled={saving || undefined}>{saving ? 'Saving…' : props.saveLabel}</Button>
        <Button type="button" variant="ghost" compact onClick={() => { returnToOpener.current = true; setOpen(false); }}>Cancel</Button>
      </span>
    </form>
  );
}

const JURISDICTION_CODE = /^[A-Z]{2,6}(-[A-Z0-9]{1,8}){0,2}$/;
const httpsUrl = (v: string) => { try { return new URL(v).protocol === 'https:'; } catch { return false; } };

// ── The panel ────────────────────────────────────────────────────────────────

export interface ControlImpactPanelProps {
  ci: ControlImpactView | null;
  /** The law's readable title (kept for screen readers in the heading). */
  lawTitle: string;
  jurisdiction?: string | null;
  sourceUrl?: string | null;
  idPrefix: string;
  /** Re-run is offered only when the user can write; resolves to the new analysis. */
  onRerun?: () => Promise<ControlImpactView | null>;
  /** "Correct jurisdiction" on the mismatch flag (editors). */
  onCorrectJurisdiction?: (jurisdiction: string) => Promise<void>;
  /** "Add the statute link" on the secondary-source flag (editors). */
  onSetSourceUrl?: (url: string) => Promise<void>;
  /** The page's existing live region. */
  announce?: (message: string) => void;
  /** While approving, the ticks decide what is filed. */
  choosing?: boolean;
  kept?: ReadonlySet<number>;
  onToggle?: (index: number, keep: boolean) => void;
  /** Without an analysis: "File a new control for this law". */
  lawControl?: boolean;
  onLawControl?: (keep: boolean) => void;
}

export function ControlImpactPanel(props: ControlImpactPanelProps) {
  const { ci, lawTitle, jurisdiction, sourceUrl, idPrefix, onRerun, onCorrectJurisdiction, onSetSourceUrl, announce, choosing, kept, onToggle } = props;
  const headingId = `${idPrefix}-heading`;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  const rerun = async () => {
    if (!onRerun || busy) return;
    setBusy(true);
    setNote(null);
    let message: string;
    try {
      const next = await onRerun();
      message = next?.status === 'ready' ? 'Analysis updated' : next?.status === 'pending' ? 'Analysis running' : noAnalysisWords(next);
    } catch {
      message = 'The analysis couldn’t finish. Try again.';
    }
    setBusy(false);
    setNote(message);
    announce?.(message);
    setTimeout(() => document.getElementById(headingId)?.focus(), 0);
  };
  const rerunButton = (quiet: boolean) => (onRerun ? (
    <Button variant={quiet ? 'ghost' : 'secondary'} compact aria-disabled={busy || undefined} onClick={() => void rerun()}>
      {busy ? 'Analysing…' : 'Re-run analysis'}
    </Button>
  ) : null);
  const sourceLink = sourceUrl ? (
    <a className="ci-source" href={sourceUrl} target="_blank" rel="noopener noreferrer">
      Open the source<span aria-hidden="true"> ↗</span><span className="sr-only"> (opens in a new tab)</span>
    </a>
  ) : null;

  const shell = (state: string, body: ReactNode) => (
    <section id={idPrefix} className="ci-panel" aria-labelledby={headingId} aria-busy={busy || undefined} data-ci-state={state}>
      <h3 id={headingId} className="ci-heading" tabIndex={-1}>Control impact<span className="sr-only">{`: ${lawTitle}`}</span></h3>
      {note ? <p className="ci-note" data-part="ci-note">{note}</p> : null}
      {body}
    </section>
  );

  if (!ci || ci.status !== 'ready') {
    const pending = ci?.status === 'pending';
    const tickId = `${idPrefix}-law-control`;
    return shell(ci?.status ?? 'none', (
      <>
        <p className="ci-line">
          <Pair glyph={pending ? 'clock' : ci ? 'alert' : 'circle'}>{noAnalysisWords(ci)}</Pair>
          {rerunButton(false)}
        </p>
        {choosing && !pending ? (
          <fieldset className="ci-fieldset">
            <legend className="ci-legend">What to file</legend>
            <span className="ci-pick">
              <input id={tickId} type="checkbox" className="ci-check" checked={!!props.lawControl} onChange={(e) => props.onLawControl?.(e.target.checked)} />
              <label htmlFor={tickId}>File a new control for this law</label>
            </span>
          </fieldset>
        ) : null}
        {sourceLink ? <p className="ci-line ci-dim">{sourceLink}</p> : null}
      </>
    ));
  }

  const analysed = ci.duties.map((d, i) => ({ d, i })).filter(({ d }) => d.outcome !== 'unanalysed');
  const over = ci.duties.map((d, i) => ({ d, i })).filter(({ d }) => d.outcome === 'unanalysed');
  const count = (o: ImpactDuty['outcome']) => ci.duties.filter((d) => d.outcome === o).length;
  const summary = ci.reason === 'already_covered'
    ? `Already covered by ${ci.already_covered_by.join(', ') || 'existing content'}`
    : ci.reason === 'no_deployer_duties'
      ? 'No duties for AI deployers, developers or providers'
      : [
          count('new') ? `${count('new')} new` : null,
          count('amend') ? `${count('amend')} amend` : null,
          count('covered') ? `${count('covered')} covered` : null,
          count('not_deployer') ? `${count('not_deployer')} not a deployer duty` : null,
          count('unanalysed') ? `${count('unanalysed')} not analysed` : null,
        ].filter(Boolean).join(' · ');

  const dutyItem = ({ d, i }: { d: ImpactDuty; i: number }) => {
    const box = `${idPrefix}-duty-${i}`;
    const canTick = choosing && d.outcome !== 'not_deployer';
    const passage = d.source_passage?.trim();
    // Covered and not-a-deployer duties create no work: one line each, the rest behind "Show duty".
    const collapsible = d.outcome === 'covered' || d.outcome === 'not_deployer';
    const open = !collapsible || expanded.has(i);
    return (
      <li key={i} className={`ci-duty ci-duty--${d.outcome}`} data-duty={i}>
        <span className="ci-line">
          <Pair id={`${box}-outcome`} glyph={DUTY_GLYPH[d.outcome]} className="ci-outcome" data-outcome={d.outcome}>{outcomeWords(d)}</Pair>
          {collapsible && passage && !open ? <span className="ci-muted">{isSectionRef(passage) ? passage : ''}</span> : null}
          {collapsible ? (
            <Button
              variant="ghost"
              compact
              aria-expanded={open}
              aria-controls={`${box}-detail`}
              onClick={() => setExpanded((prev) => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
            >
              {open ? 'Hide duty' : 'Show duty'}
            </Button>
          ) : null}
        </span>
        <span id={`${box}-detail`} className="ci-duty-body" hidden={!open}>
          <span id={`${box}-text`}>{d.text}</span>
          {passage ? (
            <span className="ci-passage">{isSectionRef(passage) ? `Source: ${passage}` : <q className="ci-quote">{passage}</q>}</span>
          ) : null}
          {d.outcome === 'unanalysed' ? null : (
            <span className="ci-dim">
              {d.low_confidence ? <Pair glyph="warning" className="ci-low">Low confidence</Pair> : `Confidence ${confidenceWord(d.confidence)}`}
            </span>
          )}
        </span>
        {canTick ? (
          <span className="ci-pick">
            <input
              id={box}
              type="checkbox"
              className="ci-check"
              checked={kept?.has(i) ?? false}
              aria-labelledby={`${box}-action ${box}-text`}
              aria-describedby={`${box}-outcome`}
              onChange={(e) => onToggle?.(i, e.target.checked)}
            />
            <label htmlFor={box} id={`${box}-action`}>{tickAction(d)}<span className="sr-only">:</span></label>
          </span>
        ) : null}
      </li>
    );
  };
  const limit = analysed.length;
  const lists = (
    <>
      {analysed.length ? <ul className="ci-duties">{analysed.map(dutyItem)}</ul> : null}
      {over.length ? (
        <>
          <h5 className="ci-subhead">{`Not analysed (over the limit of ${limit})`}</h5>
          <ul className="ci-duties">{over.map(dutyItem)}</ul>
        </>
      ) : null}
    </>
  );

  return shell('ready', (
    <>
      <div className="ci-head">
        <span className="ci-suggested" data-part="ci-suggested">Suggested: {ci.suggested ? CONTROL_IMPACT_LABEL[ci.suggested] : 'none'}</span>
        {summary ? <span className="ci-muted">{summary}</span> : null}
      </div>

      {ci.flags.includes('jurisdiction_mismatch') ? (
        <div className="ci-line ci-flag" data-part="ci-flag-jurisdiction">
          <Pair glyph="warning">{`Filed as ${jurisdiction || 'a country'}, but the bill number looks like a state law`}</Pair>
          {onCorrectJurisdiction ? (
            <InlineFix
              id={`${idPrefix}-jurisdiction`}
              openLabel="Correct jurisdiction"
              fieldLabel={`Jurisdiction${jurisdiction ? ` (now ${jurisdiction})` : ''}`}
              hint="A code like US-CA"
              saveLabel="Save jurisdiction"
              normalise={(v) => v.trim().toUpperCase()}
              validate={(v) => (JURISDICTION_CODE.test(v) ? null : 'Use a code like US, EU, US-CA or US-NY-NYC.')}
              onSave={async (j) => { await onCorrectJurisdiction(j); announce?.(`Jurisdiction set to ${j}. Analysis updated.`); }}
              focusAfterSave={headingId}
            />
          ) : null}
        </div>
      ) : null}
      {ci.flags.includes('secondary_source') ? (
        <div className="ci-line ci-flag" data-part="ci-flag-source">
          <Pair glyph="warning">Read from a summary, not the statute text: confidence capped</Pair>
          {onSetSourceUrl ? (
            <InlineFix
              id={`${idPrefix}-source-url`}
              openLabel="Add the statute link"
              fieldLabel="Link to the statute text"
              hint="An https link to the law itself"
              saveLabel="Save link"
              inputType="url"
              validate={(v) => (httpsUrl(v) ? null : 'Use an https link.')}
              onSave={async (u) => { await onSetSourceUrl(u); announce?.('Statute link saved. Analysis updated.'); }}
              focusAfterSave={headingId}
            />
          ) : null}
        </div>
      ) : null}

      {ci.duties.length > 0 ? (
        choosing ? (
          <fieldset className="ci-fieldset">
            <legend className="ci-legend">{`What to file (${ci.duties.length} duties)`}</legend>
            {lists}
          </fieldset>
        ) : (
          <>
            <h4 className="ci-duties-title">{`Duties found (${ci.duties.length})`}</h4>
            {lists}
          </>
        )
      ) : null}
      {ci.duties_over_cap > 0 && over.length === 0 ? (
        <p className="ci-line ci-muted">
          <span>{`+${ci.duties_over_cap} more not analysed: re-run to list them`}</span>
        </p>
      ) : null}

      <p className="ci-line ci-dim">
        <span>{`Analysed ${fmtDate(ci.analyzed_at)}`}</span>
        {sourceLink}
        {rerunButton(true)}
      </p>
    </>
  ));
}

/** "Changed from the suggestion (file 1 spec amendment + 1 new control). Now: file nothing." */
export function OverrideNote({ suggested, now, onUse }: { suggested: string | null; now: string | null; onUse: () => void }) {
  return (
    <p className="ci-line ci-override" data-part="ci-override">
      <Pair glyph="pause">{`Changed from the suggestion (file ${suggested ?? 'nothing'}). Now: file ${now ?? 'nothing'}.`}</Pair>
      <Button variant="ghost" compact onClick={onUse}>Use the suggestion</Button>
    </p>
  );
}

// ── After apply ──────────────────────────────────────────────────────────────

const FILED_KIND: Record<'spec_amendment' | 'new_control', [string, string]> = {
  spec_amendment: ['spec amendment', 'spec amendments'],
  new_control: ['new control', 'new controls'],
};

/** The current search with only `change` set (the other parameters are kept). */
export function changeSearch(search: string, changeId: string): string {
  const p = new URLSearchParams(search);
  p.set('change', changeId);
  return `?${p.toString()}`;
}

function filedHead(filing: ControlImpactFiling): string {
  const count = (k: 'spec_amendment' | 'new_control') => filing.filed.filter((f) => f.kind === k).length;
  return (['spec_amendment', 'new_control'] as const)
    .filter((k) => count(k) > 0)
    .map((k) => `${count(k)} ${FILED_KIND[k][count(k) === 1 ? 0 : 1]}`)
    .join(', ');
}

/** "Filed: 1 spec amendment, 1 new control", each linked to its proposal in the queue; then what was not filed. */
export function FiledSummary({ filing, duties }: { filing: ControlImpactFiling; duties: readonly ImpactDuty[] }) {
  const location = useLocation();
  const { filed, skipped } = filing;
  if (filed.length === 0 && skipped.length === 0) return null;
  const name = (i: number, kind: 'spec_amendment' | 'new_control') => {
    const d = duties[i];
    return (kind === 'new_control' ? d?.draft_control?.title : d?.control_title) ?? d?.text ?? 'This law';
  };
  return (
    <div className="ci-filed" data-part="ci-filed">
      {filed.length > 0 ? (
        <>
          <span>{`Filed: ${filedHead(filing)}`}</span>
          <ul>
            {filed.map((f) => (
              <li key={f.change_id}>
                <Link to={{ search: changeSearch(location.search, f.change_id) }}>
                  {`${f.kind === 'spec_amendment' ? 'Spec amendment' : 'New control'}: ${name(f.duty, f.kind)}`}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {skipped.length > 0 ? (
        <>
          <span>{`Not filed: ${skipped.length}`}</span>
          <ul>
            {skipped.map((s) => (
              <li key={s.duty}>{`${duties[s.duty]?.text ?? `Duty ${s.duty + 1}`}: ${s.reason}`}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

export interface FiledNoticeData { title: string; filing: ControlImpactFiling; duties: ImpactDuty[] }

/**
 * After apply the Confirm button is gone, so focus moves here; the region is named by its one-line summary, which is
 * what a screen reader reads (announced once, by focus; not a live region as well).
 */
export function FiledNotice({ notice, onDismiss }: { notice: FiledNoticeData | null; onDismiss: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!notice || !ref.current) return;
    ref.current.scrollIntoView?.({ block: 'nearest' });
    ref.current.focus();
  }, [notice]);
  if (!notice) return null;
  const line = [
    notice.filing.filed.length ? `Filed: ${filedHead(notice.filing)}.` : 'Nothing filed.',
    notice.filing.skipped.length ? `Not filed: ${notice.filing.skipped.length}.` : null,
  ].filter(Boolean).join(' ');
  return (
    <div ref={ref} role="group" aria-label={`${notice.title}. ${line}`} tabIndex={-1} className="ci-notice" data-part="ci-filed-notice">
      <span className="ci-notice-head">
        <Pair glyph="check">{notice.title}</Pair>
        <Button variant="ghost" compact onClick={onDismiss}>Dismiss</Button>
      </span>
      <FiledSummary filing={notice.filing} duties={notice.duties} />
    </div>
  );
}

/** "Control impact suggestions kept: X of Y (Z changed)"; nothing until a choice is recorded. */
export function ControlImpactStatsLine({ stats }: { stats: ControlImpactStats | null | undefined }) {
  if (!stats || stats.decided === 0) return null;
  return (
    <p className="ci-stat" data-part="ci-stats">
      {`Control impact suggestions kept: ${stats.matched} of ${stats.decided} (${stats.overridden} changed)`}
    </p>
  );
}
